import { Alert, Button, Checkbox, Form, InputNumber, message, Statistic, Table } from "antd";
import { httpsCallable } from "firebase/functions";
import { useMemo, useState } from "react";
import { functions } from "../firebase";
import { FinanceEntryType } from "../typeDefs";
import { getPersonalApportionmentAmount } from "./financeApportionment";
import { isInGstRegisteredPeriod } from "./taxConfig";

type TaxBracket = { over: number; upTo: number; rate: number };

// NZ resident individual income tax rates. These are based on IRD published
// annual rates. The 2025 year uses composite rates because threshold changes
// took effect part way through that tax year. 2026+ uses the current brackets.
const TAX_TABLES: Record<string, { label: string; brackets: TaxBracket[] }> = {
  old: {
    label: "Pre 31 Jul 2024 rates",
    brackets: [
      { over: 0, upTo: 14000, rate: 0.105 },
      { over: 14000, upTo: 48000, rate: 0.175 },
      { over: 48000, upTo: 70000, rate: 0.3 },
      { over: 70000, upTo: 180000, rate: 0.33 },
      { over: 180000, upTo: Infinity, rate: 0.39 },
    ],
  },
  fy2025: {
    label: "2024/2025 composite IRD annual rates",
    brackets: [
      { over: 0, upTo: 14000, rate: 0.105 },
      { over: 14000, upTo: 15600, rate: 0.1282 },
      { over: 15600, upTo: 48000, rate: 0.175 },
      { over: 48000, upTo: 53500, rate: 0.2164 },
      { over: 53500, upTo: 70000, rate: 0.3 },
      { over: 70000, upTo: 78100, rate: 0.3099 },
      { over: 78100, upTo: 180000, rate: 0.33 },
      { over: 180000, upTo: Infinity, rate: 0.39 },
    ],
  },
  current: {
    label: "2025/2026+ IRD annual rates",
    brackets: [
      { over: 0, upTo: 15600, rate: 0.105 },
      { over: 15600, upTo: 53500, rate: 0.175 },
      { over: 53500, upTo: 78100, rate: 0.3 },
      { over: 78100, upTo: 180000, rate: 0.33 },
      { over: 180000, upTo: Infinity, rate: 0.39 },
    ],
  },
};

const STUDENT_LOAN_THRESHOLDS: Record<string, number> = {
  old: 24128,
  fy2025: 24128,
  current: 24128,
};
const STUDENT_LOAN_RATE = 0.12;
const PROVISIONAL_TAX_THRESHOLD = 5000;

const money = (value: number) => `$${value.toFixed(2)}`;

const cleanForCallable = (value: any): any => {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (Array.isArray(value)) return value.map(cleanForCallable);
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value)
        .filter(([, item]) => item !== undefined)
        .map(([key, item]) => [key, cleanForCallable(item)]),
    );
  }
  return value;
};

const getTaxTableKey = (dateRange: number[]) => {
  const rangeEnd = dateRange[1];

  if (rangeEnd <= new Date("2024-04-01").getTime()) return "old";
  if (rangeEnd <= new Date("2025-04-01").getTime()) return "fy2025";
  return "current";
};

const calculateIncomeTax = (income: number, brackets: TaxBracket[]) =>
  brackets.reduce((tax, bracket) => {
    if (income <= bracket.over) return tax;
    const taxableInBracket = Math.min(income, bracket.upTo) - bracket.over;
    return tax + taxableInBracket * bracket.rate;
  }, 0);

export const PersonalTaxPlanner = ({
  data,
  dateRange,
}: {
  data: FinanceEntryType[];
  dateRange: number[];
}) => {
  const [otherIncome, setOtherIncome] = useState(0);
  const [taxPaid, setTaxPaid] = useState(0);
  const [hasStudentLoan, setHasStudentLoan] = useState(true);
  const [taxableDrawRate, setTaxableDrawRate] = useState(100);
  const [provisionalUplift, setProvisionalUplift] = useState(105);
  const [reviewing, setReviewing] = useState(false);

  const taxTableKey = getTaxTableKey(dateRange);
  const taxTable = TAX_TABLES[taxTableKey];
  const studentLoanThreshold = STUDENT_LOAN_THRESHOLDS[taxTableKey];

  const entries = useMemo(
    () =>
      data
        .filter((item) => !item.isDeleted)
        .filter((item) => item.date >= dateRange[0] && item.date < dateRange[1]),
    [data, dateRange],
  );

  const shareholderSalaryEntries = entries.filter(
    (item) =>
      item.category === "Expenses" &&
      ["Shareholder Salary", "Salaries and Wages"].includes(item.subCategory),
  );

  const drawingsEntries = entries.filter((item) => {
    const searchable = `${item.vendor || ""} ${item.description || ""} ${item.subCategory || ""}`.toLowerCase();

    return (
      (item.category === "Equity" && item.subCategory === "Shareholder Drawings") ||
      searchable.includes("shareholder drawing") ||
      searchable.includes("shareholder drawings") ||
      searchable.includes("owner draw") ||
      searchable.includes("drawings")
    );
  });

  const personalApportionmentEntries = entries
    .filter((item) => getPersonalApportionmentAmount(item) > 0)
    .map((item) => ({
      ...item,
      id: `${item.id}-personal-apportionment`,
      subCategory: "Shareholder Drawings (personal apportionment)",
      description: `${item.description || item.vendor} - personal portion of apportioned business-account spend`,
      amount: getPersonalApportionmentAmount(item),
      yearEndTreatment: "Shareholder Salary" as const,
      isPersonalCardSpend: true,
    }));

  const personalSourceEntries = [...shareholderSalaryEntries, ...drawingsEntries, ...personalApportionmentEntries].filter(
    (entry, index, list) => list.findIndex((item) => item.id === entry.id) === index,
  );

  const shareholderSalary = shareholderSalaryEntries.reduce((total, item) => total + item.amount, 0);
  const directDrawings = drawingsEntries.reduce((total, item) => total + item.amount, 0);
  const apportionedPersonalDrawings = personalApportionmentEntries.reduce((total, item) => total + item.amount, 0);
  const drawings = directDrawings + apportionedPersonalDrawings;
  const yearEndSalaryFromDrawings = [...drawingsEntries, ...personalApportionmentEntries]
    .filter((item) => item.yearEndTreatment === "Shareholder Salary" || item.isPersonalCardSpend)
    .reduce((total, item) => total + item.amount, 0);
  const taxableDrawings = drawings * (taxableDrawRate / 100);
  const taxableIncome = shareholderSalary + taxableDrawings + otherIncome;
  const incomeTax = calculateIncomeTax(taxableIncome, taxTable.brackets);
  const studentLoan = hasStudentLoan
    ? Math.max(0, taxableIncome - studentLoanThreshold) * STUDENT_LOAN_RATE
    : 0;
  const likelyBalance = incomeTax + studentLoan - taxPaid;
  const likelyProvisionalTax =
    incomeTax > PROVISIONAL_TAX_THRESHOLD ? incomeTax * (provisionalUplift / 100) : 0;

  const gstCollected = entries
    .filter((item) => item.category === "Revenue" && isInGstRegisteredPeriod(item.date) && item.gstIncluded && item.gstRegistered)
    .reduce((total, item) => total + (item.amount * 3) / 23, 0);
  const gstClaimable = entries
    .filter(
      (item) =>
        (item.category === "Expenses" || item.category === "Assets") &&
        isInGstRegisteredPeriod(item.date) &&
        item.gstIncluded &&
        item.gstRegistered,
    )
    .reduce((total, item) => total + ((item.amount * 3) / 23) * ((item.apportionRate ?? 100) / 100), 0);
  const revenue = entries.filter((item) => item.category === "Revenue").reduce((total, item) => total + item.amount, 0);
  const expenses = entries.filter((item) => item.category === "Expenses").reduce((total, item) => total + item.amount, 0);
  const assets = entries.filter((item) => item.category === "Assets");
  const homeOffice = entries.filter((item) => item.isHomeOfficeExpense);
  const missingReceipts = entries.filter(
    (item) => (item.category === "Expenses" || item.category === "Assets") && !item.receipt,
  );
  const unreconciled = entries.filter((item) => !item.bankTransactionId);

  const runTaxReview = async () => {
    setReviewing(true);
    try {
      const callable = httpsCallable(functions, "taxReview");
      const taxYearLabel = `${new Date(dateRange[0]).toLocaleDateString("en-GB")} - ${new Date(
        dateRange[1],
      ).toLocaleDateString("en-GB")}`;
      const payload = {
        dateRange: [dateRange[0], dateRange[1]],
        taxYearLabel,
        summary: {
          revenue,
          expenses,
          profitEstimate: revenue - expenses,
          assetPurchases: assets.reduce((total, item) => total + item.amount, 0),
          transactionCount: entries.length,
          missingReceiptCount: missingReceipts.length,
          unreconciledCount: unreconciled.length,
        },
        gst: {
          gstCollected,
          gstClaimable,
          gstPayableOrRefundable: gstCollected - gstClaimable,
        },
        personalTax: {
          shareholderSalary,
          drawings,
          directDrawings,
          apportionedPersonalDrawings,
          yearEndSalaryFromDrawings,
          taxableDrawRate,
          taxableDrawings,
          otherIncome,
          taxableIncome,
          incomeTax,
          taxPaid,
          hasStudentLoan,
          studentLoan,
          likelyBalance,
          likelyProvisionalTax,
          provisionalUplift,
          taxTable: taxTable.label,
          taxBrackets: taxTable.brackets.map((bracket) => ({
            ...bracket,
            upTo: Number.isFinite(bracket.upTo) ? bracket.upTo : null,
          })),
        },
        transactions: entries.map((item) => ({
          id: item.id,
          date: item.date,
          vendor: item.vendor,
          description: item.description,
          category: item.category,
          subCategory: item.subCategory,
          amount: item.amount,
          originalAmount: item.originalAmount,
          gstIncluded: item.gstIncluded,
          gstRegistered: item.gstRegistered,
          gstNumber: item.gstNumber,
          receipt: item.receipt,
          image: item.image,
          bankTransactionId: item.bankTransactionId,
          businessUseRate: item.businessUseRate,
          isHomeOfficeExpense: item.isHomeOfficeExpense,
          reconciliationConfidence: item.reconciliationConfidence,
        })),
        assets,
        homeOffice,
        warnings: [
          ...missingReceipts.map((item) => `Missing receipt: ${item.vendor} ${money(item.amount)}`),
          ...unreconciled.map((item) => `No bank import id: ${item.vendor} ${money(item.amount)}`),
        ],
      };

      const result = await callable(cleanForCallable(payload));
      const data = result.data as { id?: string };
      message.success(`Tax review saved${data.id ? `: ${data.id}` : ""}.`);
    } catch (error) {
      console.error(error);
      message.error(error instanceof Error ? error.message : "Tax review failed.");
    } finally {
      setReviewing(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <Alert
        type="info"
        showIcon
        message="Personal estimates only"
        description={`This is a planning estimate, not tax advice. Using ${taxTable.label}. Shareholder salary, drawings, PAYE, dividends, and company structure should be confirmed with an accountant.`}
      />

      <Form layout="vertical">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
          <Form.Item label="Other personal taxable income">
            <InputNumber min={0} value={otherIncome} onChange={(value) => setOtherIncome(Number(value || 0))} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item label="Tax/PAYE already paid">
            <InputNumber min={0} value={taxPaid} onChange={(value) => setTaxPaid(Number(value || 0))} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item label="Taxable % of drawings">
            <InputNumber min={0} max={100} value={taxableDrawRate} onChange={(value) => setTaxableDrawRate(Number(value || 0))} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item label="Provisional tax uplift %">
            <InputNumber min={100} max={120} value={provisionalUplift} onChange={(value) => setProvisionalUplift(Number(value || 105))} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item label="Student loan">
            <Checkbox checked={hasStudentLoan} onChange={(event) => setHasStudentLoan(event.target.checked)}>
              Estimate repayments
            </Checkbox>
          </Form.Item>
        </div>
      </Form>

      <Button type="primary" loading={reviewing} onClick={runTaxReview}>
        Send selected tax year to AI tax review
      </Button>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 16 }}>
        <Statistic title="Shareholder salary" value={shareholderSalary} precision={2} prefix="$" />
        <Statistic title="Drawings taken" value={drawings} precision={2} prefix="$" />
        <Statistic title="Personal apportionment drawings" value={apportionedPersonalDrawings} precision={2} prefix="$" />
        <Statistic title="Year-end salary from personal spend" value={yearEndSalaryFromDrawings} precision={2} prefix="$" />
        <Statistic title="Estimated taxable income" value={taxableIncome} precision={2} prefix="$" />
        <Statistic title="Estimated income tax" value={incomeTax} precision={2} prefix="$" />
        <Statistic title="Estimated student loan" value={studentLoan} precision={2} prefix="$" />
        <Statistic title={likelyBalance >= 0 ? "Likely amount to pay" : "Likely refund"} value={Math.abs(likelyBalance)} precision={2} prefix="$" />
        <Statistic title="Future provisional tax estimate" value={likelyProvisionalTax} precision={2} prefix="$" />
      </div>

      <Table
        title={() => "Tax rates used"}
        size="small"
        pagination={false}
        dataSource={taxTable.brackets.map((bracket) => ({
          key: `${bracket.over}-${bracket.upTo}`,
          incomeBand: bracket.upTo === Infinity ? `Over ${money(bracket.over)}` : `${money(bracket.over)} - ${money(bracket.upTo)}`,
          rate: `${(bracket.rate * 100).toFixed(2)}%`,
        }))}
        columns={[
          { title: "Income band", dataIndex: "incomeBand" },
          { title: "Tax rate", dataIndex: "rate" },
        ]}
      />

      <Table
        title={() =>
          `Personal draw / salary source transactions (${personalSourceEntries.length} found)`
        }
        size="small"
        pagination={{ pageSize: 8 }}
        locale={{
          emptyText:
            "No shareholder salary, salaries/wages, drawings, or personal apportionment entries found in the selected date range. Check the selected tax year/date range or categorise entries as Expenses / Shareholder Salary, Expenses / Salaries and Wages, Equity / Shareholder Drawings, or set an expense/asset claim percentage below 100%."
        }}
        dataSource={personalSourceEntries.map((item) => ({ ...item, key: item.id }))}
        columns={[
          { title: "Date", dataIndex: "date", render: (value) => new Date(value).toLocaleDateString("en-GB") },
          { title: "Type", dataIndex: "subCategory" },
          { title: "Description", dataIndex: "description" },
          {
            title: "Year-end treatment",
            render: (_value, record) =>
              record.yearEndTreatment || (record.isPersonalCardSpend ? "Shareholder Salary" : "-"),
          },
          { title: "Amount", dataIndex: "amount", render: money },
        ]}
      />
    </div>
  );
};
